import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { idToLink } from 'inside-out/managers/navManager';

import { ArrowLink } from 'inside-out/components/shared/arrowLink';
import { Cards } from 'inside-out/components/shared/cards';
import { Link } from 'inside-out/components/shared/link';
import { PageHeader } from 'inside-out/components/shared/pageHeader';
import { PolicyCard } from 'inside-out/components/shared/cards/policyCard';
import HeaderNSWIcon from 'inside-out/images/policy-service-development/header-nsw-orange.svg';

import {
	wrapperStyle,
	innerWrapperStyle,
	titleStyle,
	textStyle,
	subTitleStyle,
	contentSectionStyle,
	cardSectionStyle,
	roleSectionStyle,
	quadrantsWrapperStyle,
	coordinatorsButtonSectionStyle,
	coordinatorsButtonStyle,
} from 'inside-out/page-styles/policy-and-service-development';

export default class PolicyServiceDevelopmentPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							main_heading: PropTypes.string.isRequired,
							heading: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
							main_attachments: PropTypes.arrayOf(
								PropTypes.shape({
									title: PropTypes.string.isRequired,
									file: PropTypes.string.isRequired,
								})
							).isRequired,
							additional_body: PropTypes.string.isRequired,
							coordinators_heading: PropTypes.string.isRequired,
							coordinators_body: PropTypes.string.isRequired,
							coordinators_attachment: PropTypes.shape({
								title: PropTypes.string.isRequired,
								file: PropTypes.string.isRequired,
							}),
							left_heading: PropTypes.string.isRequired,
							left_body: PropTypes.string.isRequired,
							left_link_text: PropTypes.string.isRequired,
							left_link_url: PropTypes.string.isRequired,
							right_heading: PropTypes.string.isRequired,
							right_body: PropTypes.string.isRequired,
							right_link_text: PropTypes.string.isRequired,
							right_link_url: PropTypes.string.isRequired,
						}),
					})
				),
			}),

			nswPolicies: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							id: PropTypes.string,
							title: PropTypes.string,
							type: PropTypes.string,
							date: PropTypes.string,
						}).isRequired,
					}).isRequired
				),
			}).isRequired,

			steeringCommitteePolicies: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							id: PropTypes.string,
							title: PropTypes.string,
							type: PropTypes.string,
							date: PropTypes.string,
						}).isRequired,
					}).isRequired
				),
			}).isRequired,
		}),
	};

	render() {
		const { nswPolicies, steeringCommitteePolicies } = this.props.data;
		const { node } = this.props.data.page.edges[0];

		return (
			<div className={wrapperStyle}>
				<Helmet title={node.main_heading} />
				<Helmet title={node.main_heading}>
          <meta property="og:type" content="website"/>
          <meta property="og:title" content={node.main_heading}/>
          <meta name="description" content=""/>
          <meta property="og:description" content=""/>
        </Helmet>

				<PageHeader
					background="#F26C22"
					titleText={node.main_heading}
					icon={HeaderNSWIcon}
					titleTextColor="#fff"
				/>
				<section className={contentSectionStyle('#fff')}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{node.heading}</h1>
						</header>
						<div className={textStyle} dangerouslySetInnerHTML={{ __html: node.body }} />
						<section className={cardSectionStyle()}>
							<Cards columns={4}>
								{node.main_attachments.map((attachment, index) => (
									<PolicyCard key={index} href={attachment.file} title={attachment.title} />
								))}
							</Cards>
						</section>
						<div
							className={textStyle}
							dangerouslySetInnerHTML={{ __html: `<p>${node.additional_body}</p>` }}
						/>
					</div>
				</section>
				<section className={contentSectionStyle()}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{node.coordinators_heading}</h1>
						</header>
						<div className={roleSectionStyle}>
							<div
								className={textStyle}
								dangerouslySetInnerHTML={{ __html: node.coordinators_body }}
							/>

							<PolicyCard
								href={node.coordinators_attachment.file}
								title={node.coordinators_attachment.title}
							/>
						</div>
						<div className={coordinatorsButtonSectionStyle}>
							<Link to={node.coordinators_button_url} className={coordinatorsButtonStyle}>
								{node.coordinators_button_text}
							</Link>
						</div>
					</div>
				</section>
				<div className={innerWrapperStyle}>
					<div className={quadrantsWrapperStyle}>
						<section>
							<h1>{node.left_heading}</h1>
							<div className={textStyle} dangerouslySetInnerHTML={{ __html: node.left_body }} />
							{node.left_link_url && (
								<ArrowLink to={node.left_link_url}>{node.left_link_text}</ArrowLink>
							)}
						</section>
						<section>
							<h1>{node.right_heading}</h1>
							<div className={textStyle} dangerouslySetInnerHTML={{ __html: node.right_body }} />
							{node.right_link_url && (
								<ArrowLink to={node.right_link_url}>{node.right_link_text}</ArrowLink>
							)}
						</section>
					</div>
				</div>
				<section className={contentSectionStyle('#505E64')}>
					<div className={innerWrapperStyle}>
						<div className={cardSectionStyle()} id="latest-updates">
							<header>
								<h1 className={subTitleStyle}>Latest updates on service plan</h1>
								<Link to="/policy-and-service-development/updates-on-service-plan-in-nsw">
									View All Updates
								</Link>
							</header>

							<Cards columns={4}>
								{nswPolicies.edges.map(({ node }) => (
									<PolicyCard
										key={node.id}
										href={node.attachments[0].file}
										title={node.title}
										date={node.date}
									/>
								))}
							</Cards>
						</div>
					</div>
				</section>
				<section className={contentSectionStyle('#fff')}>
					<div className={innerWrapperStyle}>
						<div className={cardSectionStyle()} id="communication">
							<header>
								<h1 className={subTitleStyle}>
									Communication from the State Wide Steering Committee
								</h1>
								<Link to="/policy-and-service-development/steering-committee">View All</Link>
							</header>
							<Cards columns={4}>
								{steeringCommitteePolicies.edges.map(({ node }) => (
									<PolicyCard
										key={node.id}
										href={node.attachments[0].file}
										title={node.title}
										date={node.date}
									/>
								))}
							</Cards>
						</div>
					</div>
				</section>
			</div>
		);
	}
}

export const query = graphql`
	query PolicyServiceDevelopmentNSWPageQuery {
		page: allContentJson(
			filter: { id: { regex: "/content\\/policy-and-service-development-nsw-page\\.json/" } }
		) {
			edges {
				node {
					main_heading
					heading
					body
					main_attachments {
						title
						file
					}
					additional_body
					coordinators_heading
					coordinators_body
					coordinators_button_text
					coordinators_button_url
					coordinators_attachment {
						title
						file
					}
					left_heading
					left_body
					left_link_text
					left_link_url
					right_heading
					right_body
					right_link_text
					right_link_url
				}
			}
		}

		nswPolicies: allPolicyAndServiceDevelopmentJson(
			filter: { type: { eq: "service-plan-nsw" } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			edges {
				node {
					id
					title
					date
					attachments {
						file
					}
				}
			}
		}

		steeringCommitteePolicies: allPolicyAndServiceDevelopmentJson(
			filter: { type: { eq: "steering-committee" } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			edges {
				node {
					id
					title
					date
					attachments {
						file
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/policy-and-service-development/nsw/index.js